import React, { useState, useCallback, useMemo } from 'react';
import { XStack, Card, H2, Paragraph, Text, Button, YStack, SizableText, H4 } from "tamagui";
import AchievementBackground from '@assets/--native/AchievementBackground';
import { useNavigation } from '@react-navigation/native';
import LottieView from 'lottie-react-native';
import { Alert, View, Pressable, Platform, useWindowDimensions } from 'react-native';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';
import styles from '@stylesheet';

// Helper function to parse text by delimiter
const parseByDelimiter = (text: string): string => {
  if (!text) return '';
  return text.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Generate gradient colors based on course category
const getGradientColors = (category: string, isPublic: boolean, completed: boolean) => {
  if (completed) {
    return {
      colors: ['#d1fae5', '#a7f3d0'],
      start: { x: 0, y: 0 },
      end: { x: 1, y: 1 }
    };
  }

  const gradientMap = {
    'Marketing': isPublic
      ? { colors: ['#fef3c7', '#fde68a'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#fed7aa', '#fdba74'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'AI SaaS': isPublic
      ? { colors: ['#dbeafe', '#bfdbfe'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#c7d2fe', '#a5b4fc'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'Affiliate': isPublic
      ? { colors: ['#dcfce7', '#bbf7d0'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#a7f3d0', '#6ee7b7'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'Automation': isPublic
      ? { colors: ['#f3e8ff', '#e9d5ff'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#ddd6fe', '#c4b5fd'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'General': isPublic
      ? { colors: ['#f1f5f9', '#e2e8f0'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#f8fafc', '#f1f5f9'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
  };

  return gradientMap[category] || gradientMap['General'];
};

// Get dark theme gradient colors
const getDarkGradientColors = (category: string, isPublic: boolean, completed: boolean) => {
  if (completed) {
    return {
      colors: ['#064e3b', '#065f46'],
      start: { x: 0, y: 0 },
      end: { x: 1, y: 1 }
    };
  }

  const gradientMap = {
    'Marketing': isPublic
      ? { colors: ['#451a03', '#78350f'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#7c2d12', '#9a3412'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'AI SaaS': isPublic
      ? { colors: ['#1e3a8a', '#1e40af'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#312e81', '#3730a3'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'Affiliate': isPublic
      ? { colors: ['#14532d', '#166534'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#064e3b', '#065f46'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'Automation': isPublic
      ? { colors: ['#581c87', '#6b21a8'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#4c1d95', '#5b21b6'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } },
    'General': isPublic
      ? { colors: ['#1e293b', '#334155'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
      : { colors: ['#0f172a', '#1e293b'], start: { x: 0, y: 0 }, end: { x: 1, y: 1 } }
  };

  return gradientMap[category] || gradientMap['General'];
};

interface CourseItemProps {
  id: string;
  title: string;
  completed?: boolean;
  description: string;
  index?: number;
  duration?: string;
  level?: string;
  category?: string;
  isPublic?: boolean;
  author?: string;
  authorId?: string;
  currentUserId?: string | null;
  courseData?: any; // Full course data to pass to editor
  onShareCourse?: (courseData: any) => void;
  // Voting props
  voting?: {
    upvotes: number;
    downvotes: number;
    totalVotes: number;
    voteScore: number;
    voteRatio: number;
  };
  userVote?: 'upvote' | 'downvote' | null;
  onVote?: (courseId: string, voteType: 'upvote' | 'downvote') => void;
}

const CourseItem: React.FC<CourseItemProps> = ({ id, title, completed = false, description, index = 0, duration = "~5 minutes", level = "Beginner", category = "General", isPublic = true, author = "SmartSaaS Team", authorId = "system", currentUserId = null, courseData, onShareCourse, voting, userVote = null, onVote }) => {
  const navigation = useNavigation();
  const theme = themeSelector();
  const { width } = useWindowDimensions();

  // Responsive variables
  const isSmallScreen = width < 600;
  const showCompactMode = isSmallScreen;

  // Permission check - only show edit/delete for course owner
  const canEdit = currentUserId && authorId && currentUserId === authorId;

  // Action handlers
  const handleLearnPress = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      (navigation as any).navigate("Courses", {
        params: { id, title, description }
      });
    } else {
      Alert.alert("Coming Soon", "This feature is not available yet.");
    }
  }, [navigation, id, title, description]);

  const handleEditPress = useCallback(() => {
    Alert.alert(
      "Edit Course",
      `Edit "${title}" course content, settings, and materials.`,
      [
        { text: "Cancel", style: "cancel" },
        { 
          text: "Edit",
          onPress: () => {
            // Navigate to course editor
            console.log("Editing course:", title);
            Alert.alert("Edit Mode", "Course editor will open here");
          }
        }
      ]
    );
  }, [title]);

  const handleDeletePress = useCallback(() => {
    Alert.alert(
      "Delete Course",
      `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: () => {
            console.log("Deleting course:", title);
            Alert.alert("Course Deleted", `"${title}" has been deleted`);
          }
        }
      ]
    );
  }, [title]);

  const handleSharePress = useCallback(() => {
    if (onShareCourse && id) {
      const courseData = {
        _id: id,
        title: title,
        dataTitle: title,
        description: description,
        category: category,
        level: level,
        duration: duration
      };
      onShareCourse(courseData);
    } else {
      Alert.alert("Share Course", `Share "${title}" with others via a generated link.`);
    }
  }, [onShareCourse, id, title, description, category, level, duration]);

  const handleVotePress = useCallback((voteType: 'upvote' | 'downvote') => {
    if (onVote && id) {
      onVote(id, voteType);
    } else {
      Alert.alert("Vote on Course", `Vote ${voteType === 'upvote' ? 'up' : 'down'} for "${title}".`);
    }
  }, [onVote, id, title]);

  const handleEditCourse = useCallback(() => {
    (navigation as any).navigate('CourseEditor', {
      courseId: id,
      courseTitle: title,
      courseData: courseData,
      mode: 'edit'
    });
  }, [navigation, id, title, courseData]);

  const handleLearnCourse = useCallback(() => {
    (navigation as any).navigate('CourseEditor', {
      courseId: id,
      courseTitle: title,
      courseData: courseData,
      mode: 'learn'
    });
  }, [navigation, id, title, courseData]);

  // Get gradient colors based on theme
  const gradientConfig = theme
    ? getGradientColors(category, isPublic, completed)
    : getDarkGradientColors(category, isPublic, completed);

  return useMemo(() => (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      delay={index * 100}
      style={[
        styles.modernCard,
        {
          width: showCompactMode ? 320 : 380,
          minHeight: showCompactMode ? 320 : 380,
          overflow: 'hidden',
          ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
        }
      ]}
    >
      <LinearGradient
        colors={gradientConfig.colors}
        start={gradientConfig.start}
        end={gradientConfig.end}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 20,
        }}
      />
      {/* Subtle overlay for depth */}
      <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 20,
        backgroundColor: theme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        shadowColor: theme ? '#000' : '#fff',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 8,
      }} />
      <View style={{
        minHeight: 100,
        overflow: "visible",
        backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.3)',
        borderRadius: 20,
        flex: 1
      }}>
        {/* Completion indicator */}
        {completed && (
          <View style={{
            position: 'absolute',
            zIndex: 1,
            top: 12,
            right: 12,
            backgroundColor: '#10b981',
            borderRadius: 6,
            minHeight: 12,
            minWidth: 12,
            elevation: 5,
            shadowColor: '#10b981',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.3,
            shadowRadius: 4,
          }} />
        )}

        {/* Header Section */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: showCompactMode ? 12 : 16,
          borderBottomWidth: 1,
          borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Course Icon */}
          <View style={{
            width: showCompactMode ? 36 : 44,
            height: showCompactMode ? 36 : 44,
            borderRadius: showCompactMode ? 18 : 22,
            backgroundColor: completed
              ? (theme ? '#d1fae5' : '#064e3b')
              : (theme ? '#dbeafe' : '#1e40af'),
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 2,
            borderColor: completed
              ? (theme ? '#10b981' : '#059669')
              : (theme ? '#3b82f6' : '#60a5fa'),
          }}>
            <MaterialIcons
              name={completed ? "check-circle" : "school"}
              size={showCompactMode ? 18 : 22}
              color={completed
                ? (theme ? '#059669' : '#10b981')
                : (theme ? '#1e40af' : '#93c5fd')}
            />
          </View>

          {/* Title and Metadata */}
          <View style={{ flex: 1, marginLeft: 12 }}>
            <XStack alignItems="center" gap={8}>
              <H4
                numberOfLines={2}
                color={theme ? "#1e293b" : "#f1f5f9"}
                fontSize={showCompactMode ? 16 : 18}
                fontWeight="600"
                flex={1}
                lineHeight={showCompactMode ? 20 : 22}
              >
                {parseByDelimiter(title)}
              </H4>
            </XStack>

            <XStack alignItems="center" gap={8} marginTop={4}>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {duration}
              </SizableText>
              <View style={{
                width: 4,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme ? "#64748b" : "#94a3b8",
              }} />
              <SizableText
                fontSize={12}
                color={completed
                  ? (theme ? "#059669" : "#10b981")
                  : (theme ? "#64748b" : "#94a3b8")}
                textTransform="capitalize"
              >
                {completed ? 'Completed' : 'Available'}
              </SizableText>
              <View style={{
                width: 4,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme ? "#64748b" : "#94a3b8",
              }} />
              <SizableText
                fontSize={12}
                color={isPublic
                  ? (theme ? "#3b82f6" : "#60a5fa")
                  : (theme ? "#7c3aed" : "#a78bfa")}
                textTransform="capitalize"
              >
                {isPublic ? 'Public' : 'Internal'}
              </SizableText>
            </XStack>
          </View>

          {/* Progress/Score indicator and Edit Actions */}
          <XStack alignItems="center" gap={8}>
            <View style={{
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 12,
              backgroundColor: theme ? '#f1f5f9' : '#1e293b',
            }}>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
                fontWeight="600"
              >
                {completed ? '100%' : '0%'}
              </SizableText>
            </View>

            {/* Action buttons for user-created courses */}
            {canEdit && (
              <XStack gap={4}>
                <Pressable
                  onPress={handleSharePress}
                  style={{
                    padding: 6,
                    borderRadius: 8,
                    backgroundColor: theme ? '#f0fdf4' : '#064e3b',
                  }}
                >
                  <MaterialIcons
                    name="share"
                    size={16}
                    color={theme ? '#059669' : '#10b981'}
                  />
                </Pressable>
                <Pressable
                  onPress={handleEditPress}
                  style={{
                    padding: 6,
                    borderRadius: 8,
                    backgroundColor: theme ? '#dbeafe' : '#1e40af',
                  }}
                >
                  <MaterialIcons
                    name="edit"
                    size={16}
                    color={theme ? '#1e40af' : '#93c5fd'}
                  />
                </Pressable>
                <Pressable
                  onPress={handleDeletePress}
                  style={{
                    padding: 6,
                    borderRadius: 8,
                    backgroundColor: theme ? '#fee2e2' : '#7f1d1d',
                  }}
                >
                  <MaterialIcons
                    name="delete"
                    size={16}
                    color="#ef4444"
                  />
                </Pressable>
              </XStack>
            )}
          </XStack>
        </View>

        {/* Content Section */}
        <View style={{ flex: 1, padding: showCompactMode ? 12 : 16 }}>
          {/* Description */}
          <YStack gap={12} marginBottom={16}>
          <YStack flex={1}>
              <SizableText
                fontSize={14}
                color={theme ? "#64748b" : "#94a3b8"}
                lineHeight={20}
                numberOfLines={3}
              >
                {description}
              </SizableText>
            </YStack>
          </YStack>

          {/* Course Details */}
          <YStack gap={8}>
            <XStack alignItems="center" gap={8}>
              <MaterialIcons
                name="schedule"
                size={16}
                color={theme ? "#3b82f6" : "#60a5fa"}
              />
              <SizableText
                fontSize={14}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                Duration: {duration}
              </SizableText>
            </XStack>

            <XStack alignItems="center" gap={8}>
              <MaterialIcons
                name="category"
                size={16}
                color={theme ? "#059669" : "#10b981"}
              />
              <SizableText
                fontSize={14}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                Level: {level}
              </SizableText>
            </XStack>

            <XStack alignItems="center" gap={8}>
              <MaterialIcons
                name="folder"
                size={16}
                color={theme ? "#7c3aed" : "#a78bfa"}
              />
              <SizableText
                fontSize={14}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                Category: {category}
              </SizableText>
            </XStack>

            <XStack alignItems="center" gap={8}>
              <MaterialIcons
                name={canEdit ? "person" : (isPublic ? "public" : "lock")}
                size={16}
                color={canEdit ? (theme ? "#059669" : "#10b981") : (isPublic ? (theme ? "#3b82f6" : "#60a5fa") : (theme ? "#7c3aed" : "#a78bfa"))}
              />
              <SizableText
                fontSize={14}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {canEdit ? `Created by ${author}` : `Access: ${isPublic ? 'Public' : 'Internal Only'}`}
              </SizableText>
            </XStack>
          </YStack>
        </View>

        {/* Voting Section */}
        {voting && currentUserId && (
          <View style={{
            padding: showCompactMode ? 12 : 16,
            borderTopWidth: 1,
            borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
          }}>
            <XStack alignItems="center" justifyContent="space-between" marginBottom={8}>
              <SizableText
                fontSize={14}
                fontWeight="600"
                color={theme ? "#1e293b" : "#f1f5f9"}
              >
                Community Rating
              </SizableText>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {voting.totalVotes} vote{voting.totalVotes !== 1 ? 's' : ''}
              </SizableText>
            </XStack>

            <XStack alignItems="center" justifyContent="space-between" gap={12}>
              {/* Upvote Button */}
              <Pressable
                onPress={() => handleVotePress('upvote')}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: userVote === 'upvote'
                    ? (theme ? '#d1fae5' : '#064e3b')
                    : (theme ? '#f1f5f9' : '#1e293b'),
                  borderWidth: 1,
                  borderColor: userVote === 'upvote'
                    ? (theme ? '#10b981' : '#059669')
                    : (theme ? '#e2e8f0' : '#374151'),
                  flex: 1,
                }}
              >
                <MaterialIcons
                  name="thumb-up"
                  size={16}
                  color={userVote === 'upvote'
                    ? (theme ? '#059669' : '#10b981')
                    : (theme ? '#64748b' : '#94a3b8')}
                />
                <SizableText
                  fontSize={12}
                  color={userVote === 'upvote'
                    ? (theme ? '#059669' : '#10b981')
                    : (theme ? '#64748b' : '#94a3b8')}
                  marginLeft={4}
                  fontWeight="600"
                >
                  {voting.upvotes}
                </SizableText>
              </Pressable>

              {/* Vote Score Display */}
              <View style={{
                alignItems: 'center',
                justifyContent: 'center',
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: theme ? '#f8fafc' : '#334155',
                borderWidth: 1,
                borderColor: theme ? '#e2e8f0' : '#475569',
                minWidth: 60,
              }}>
                <SizableText
                  fontSize={14}
                  fontWeight="700"
                  color={voting.voteScore > 0
                    ? (theme ? '#059669' : '#10b981')
                    : voting.voteScore < 0
                      ? '#ef4444'
                      : (theme ? '#64748b' : '#94a3b8')}
                >
                  {voting.voteScore > 0 ? '+' : ''}{voting.voteScore}
                </SizableText>
                <SizableText
                  fontSize={10}
                  color={theme ? '#64748b' : '#94a3b8'}
                >
                  Score
                </SizableText>
              </View>

              {/* Downvote Button */}
              <Pressable
                onPress={() => handleVotePress('downvote')}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: userVote === 'downvote'
                    ? (theme ? '#fee2e2' : '#7f1d1d')
                    : (theme ? '#f1f5f9' : '#1e293b'),
                  borderWidth: 1,
                  borderColor: userVote === 'downvote'
                    ? '#ef4444'
                    : (theme ? '#e2e8f0' : '#374151'),
                  flex: 1,
                }}
              >
                <MaterialIcons
                  name="thumb-down"
                  size={16}
                  color={userVote === 'downvote'
                    ? '#ef4444'
                    : (theme ? '#64748b' : '#94a3b8')}
                />
                <SizableText
                  fontSize={12}
                  color={userVote === 'downvote'
                    ? '#ef4444'
                    : (theme ? '#64748b' : '#94a3b8')}
                  marginLeft={4}
                  fontWeight="600"
                >
                  {voting.downvotes}
                </SizableText>
              </Pressable>
            </XStack>

            {/* Vote Ratio Bar */}
            {voting.totalVotes > 0 && (
              <View style={{
                marginTop: 8,
                height: 4,
                backgroundColor: theme ? '#e2e8f0' : '#374151',
                borderRadius: 2,
                overflow: 'hidden',
              }}>
                <View style={{
                  height: '100%',
                  width: `${voting.voteRatio}%`,
                  backgroundColor: theme ? '#10b981' : '#059669',
                  borderRadius: 2,
                }} />
              </View>
            )}
          </View>
        )}

        {/* Action Buttons */}
        <View style={{
          flexDirection: showCompactMode ? 'column' : 'row',
          padding: showCompactMode ? 12 : 16,
          gap: showCompactMode ? 8 : 12,
          borderTopWidth: 1,
          borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Status Badge */}
          <View style={{
            flex: showCompactMode ? undefined : 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
            borderRadius: 8,
            backgroundColor: completed
              ? (theme ? '#d1fae5' : '#064e3b')
              : (theme ? '#f1f5f9' : '#1e293b'),
          }}>
            <MaterialIcons
              name={completed ? "check-circle" : "play-circle-outline"}
              size={16}
              color={completed
                ? (theme ? "#059669" : "#10b981")
                : (theme ? "#64748b" : "#94a3b8")}
            />
            <SizableText
              fontSize={12}
              color={completed
                ? (theme ? "#059669" : "#10b981")
                : (theme ? "#64748b" : "#94a3b8")}
              marginLeft={4}
            >
              {completed ? 'Completed' : 'Available'}
            </SizableText>
          </View>

          {/* Primary Action */}
          <Pressable
            onPress={canEdit ? handleEditCourse : handleLearnCourse}
            style={{
              flex: showCompactMode ? undefined : 2,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 12,
              borderRadius: 8,
              backgroundColor: canEdit
                ? (theme ? '#ddd6fe' : '#312e81')
                : (completed
                  ? (theme ? '#f0fdf4' : '#064e3b')
                  : (theme ? '#dbeafe' : '#1e40af')),
              borderWidth: 1,
              borderColor: canEdit
                ? (theme ? '#7c3aed' : '#a78bfa')
                : (completed
                  ? (theme ? '#10b981' : '#059669')
                  : (theme ? '#3b82f6' : '#60a5fa')),
            }}
          >
            <MaterialIcons
              name={canEdit ? "edit" : (completed ? "refresh" : "play-arrow")}
              size={16}
              color={canEdit
                ? (theme ? '#7c3aed' : '#a78bfa')
                : (completed
                  ? (theme ? '#059669' : '#10b981')
                  : (theme ? '#1e40af' : '#93c5fd'))}
            />
            <SizableText
              fontSize={14}
              color={canEdit
                ? (theme ? '#7c3aed' : '#a78bfa')
                : (completed
                  ? (theme ? '#059669' : '#10b981')
                  : (theme ? '#1e40af' : '#93c5fd'))}
              marginLeft={6}
              fontWeight="600"
            >
              {canEdit ? 'Edit Course' : (completed ? 'Review' : 'Start Learning')}
            </SizableText>
          </Pressable>
        </View>
      </View>
    </Animatable.View>
  ), [id, title, completed, description, duration, level, category, isPublic, author, authorId, currentUserId, canEdit, courseData, theme, showCompactMode, handleEditCourse, handleLearnCourse, handleEditPress, handleDeletePress, handleSharePress, handleVotePress, index, gradientConfig, voting, userVote]);
};
export default CourseItem;